import { Sparkles, Zap, Shield, Clock, Rocket, Bot } from "lucide-react";
import { SITE } from "@/data/site";

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  icon: React.ElementType;
  color: string;
}

/**
 * The answers used to be collapsed behind an accordion that shipped closed, and every
 * one of them called the school "AI AutoBase" — a spelling the brand spec forbids and
 * that Google indexes as a third, separate entity. They also promised income.
 *
 * They are now plain visible text under question-form headings, because that is the
 * shape an AI search engine can lift whole, and they are the source for the FAQPage
 * JSON-LD below. One array, two consumers: the rendered answer and the structured one
 * can never drift apart.
 */
const faqData: FAQItem[] = [
  {
    id: 1,
    question: "What is AI Automation Base?",
    answer:
      "AI Automation Base (short name AI Auto Base) is an online school run by Hector Diaz, founder of Orbix Automation Solutions in Pennsylvania. It teaches people to build their own AI automation systems and keep them, rather than renting someone else's platform.",
    icon: Bot,
    color: "from-[#F84C00] to-[#FF6B35]",
  },
  {
    id: 2,
    question: "What do I get with a membership?",
    answer:
      "Live follow-along classes, courses on Claude Code, n8n and AntiGravity, the Classroom Library mirrored from Skool every day, a self-hosted n8n workflow library, a prompt library, the Resources Vault, and Build Packs that install a complete system on a machine you own.",
    icon: Sparkles,
    color: "from-[#F84C00] to-[#FFA500]",
  },
  {
    id: 3,
    question: "What is a Build Pack?",
    answer:
      "A Build Pack is an ordered set of prompts that you paste into Claude Code, one at a time, on a clean machine. At the end you have a working system — a sales department, a law firm back office, an agency stack — running on your own box against your own keys. Each pack says up front what was verified and what was not.",
    icon: Rocket,
    color: "from-[#FF6B35] to-[#F84C00]",
  },
  {
    id: 4,
    question: "Do I need to know how to code?",
    answer:
      "No, but you do need to be willing to use a terminal. The classes are follow-along: you watch the command being typed, you type it, and when it breaks the fix is part of the lesson. Mistakes are left in on purpose.",
    icon: Shield,
    color: "from-[#FFA500] to-[#F84C00]",
  },
  {
    id: 5,
    question: "What does it cost?",
    answer:
      "Membership is $20 a month, $50 a year, or $99 once for lifetime access. The full price table with what each tier includes is in the pricing section of this page. There is also a free Skool community, which is separate and priced separately.",
    icon: Clock,
    color: "from-[#F84C00] to-[#FF4500]",
  },
  {
    id: 6,
    question: "What tools will I actually use?",
    answer:
      "Claude Code, n8n, PostgreSQL, Docker, AntiGravity and the Codex SDK, mostly self-hosted on a machine or VPS you control. The n8n workflow library is a self-hosted collection you run yourself; the courses show you how to stand it up.",
    icon: Zap,
    color: "from-[#FF4500] to-[#F84C00]",
  },
];

/**
 * Exported so the homepage can hand it to <Seo>. Emitting it from the same array the
 * page renders is the only way to keep Google's "answer must be visible on the page"
 * rule true by construction.
 */
export const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${SITE.origin}/#faq`,
  mainEntity: faqData.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

export function FAQ() {
  return (
    <section
      id="faq"
      aria-labelledby="faq-heading"
      className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-[#1a0a1f] via-[#0f0514] to-black relative overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden bg-black">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-[#F84C00]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-[#F84C00]/5 rounded-full blur-3xl animate-float" style={{ animationDelay: "2s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#F84C00]/5 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F84C00]/10 rounded-full mb-4 sm:mb-6 border border-[#F84C00]/20">
            <Sparkles className="w-4 h-4 text-[#F84C00]" />
            <span className="text-sm font-medium text-[#F84C00]">Frequently Asked Questions</span>
          </div>
          <h2 id="faq-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 relative">
            <span className="text-white">Got </span>
            <span className="text-[#F84C00] relative inline-block">
              Questions?
              <div className="absolute -inset-2 bg-[#F84C00]/20 blur-2xl rounded-full -z-10"></div>
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
            Everything you need to know about AI Automation Base
          </p>
        </div>

        {/* Answers render open. An accordion that ships closed puts the answer behind an
            interaction, and the engines that matter most here never click anything. */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 items-start">
          {faqData.map((faq, index) => {
            const Icon = faq.icon;
            return (
              <article
                key={faq.id}
                className="group animate-fade-in h-full"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative h-full overflow-hidden rounded-2xl border border-white/10 bg-[#15091a] transition-colors duration-300 hover:border-[#F84C00]/40">
                  <div className="w-full text-left p-4 sm:p-5 flex items-start gap-3 sm:gap-4 relative z-10 bg-black">
                    <div
                      className={`shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${faq.color} flex items-center justify-center border-[#f04800]`}
                    >
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-semibold pr-2 leading-tight text-white group-hover:text-[#F84C00] transition-colors duration-200">
                        {faq.question}
                      </h3>
                    </div>
                  </div>

                  <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0 bg-black">
                    <div className="pl-0 sm:pl-14">
                      <div className="w-full h-px bg-[#F84C00]/40 mb-3" />
                      <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* CTA Footer with glass effect */}
        <div className="mt-12 sm:mt-16 text-center animate-fade-in" style={{ animationDelay: "0.6s" }}>
          <div className="relative inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 p-4 sm:p-6 rounded-2xl border border-white/20 backdrop-blur-xl bg-gradient-to-br from-white/10 via-[#F84C00]/10 to-white/5 shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.2)]">
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 pointer-events-none rounded-2xl" />
            <div className="absolute inset-0 bg-[#F84C00]/5 rounded-2xl blur-xl -z-10 border-primary" />
            <div className="text-center sm:text-left relative z-10">
              <p className="font-semibold text-base sm:text-lg mb-1 text-white">Still have questions?</p>
              <p className="text-sm text-gray-400">Email us and a person answers.</p>
            </div>
            {/* Was /contact, which redirects to the login wall — a dead end for anyone
                not signed in, and the one place a stranger most needs a way through. */}
            <a
              href={`mailto:${SITE.email}?subject=Question%20from%20aiautobase.com`}
              className="shrink-0 px-6 py-3 bg-[#C73D00] hover:bg-[#B53600] text-white rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(199,61,0,0.5)] relative z-10 shadow-[0_4px_12px_rgba(199,61,0,0.4)]"
            >
              {SITE.email}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
